import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiFetch } from "../api/api";
import { useAuth } from "../auth/AuthProvider";

const INVERT = new Set([
  "Expense Ratio",
  "Tracking Error",
  "Tracking Diff",
  "Tracking_Error_STD",
  "VaR95",
  "VaR99",
  "Tail_Risk",
  "Anomaly_Check_Max_Drop",
  "Volatility",
  "Max_Drawdown",
  "Avg_Spread_Proxy",
  "COVID_Drawdown",
  "Ukraine_Drawdown",
]);

const ALL_METRICS = [
  { key: "AUM (Cr)", label: "AUM", group: "Size", dir: "up" },
  { key: "Avg Volume", label: "Avg Volume", group: "Size", dir: "up" },
  { key: "Avg_Daily_Value_Rs", label: "Daily Value", group: "Size", dir: "up" },
  { key: "Expense Ratio", label: "Exp Ratio", group: "Cost", dir: "down" },
  { key: "Tracking Error", label: "Track Err", group: "Cost", dir: "down" },
  { key: "Tracking Diff", label: "Track Diff", group: "Cost", dir: "down" },
  { key: "Tracking_Error_STD", label: "TE Std", group: "Cost", dir: "down" },
  { key: "Correlation", label: "Correlation", group: "Quality", dir: "up" },
  { key: "Beta", label: "Beta", group: "Quality", dir: "up" },
  { key: "R_Squared", label: "R2", group: "Quality", dir: "up" },
  { key: "Volatility", label: "Volatility", group: "Risk", dir: "down" },
  { key: "VaR95", label: "VaR 95", group: "Risk", dir: "down" },
  { key: "VaR99", label: "VaR 99", group: "Risk", dir: "down" },
  { key: "Tail_Risk", label: "Tail Risk", group: "Risk", dir: "down" },
  { key: "Max_Drawdown", label: "Max DD", group: "Risk", dir: "down" },
  { key: "Anomaly_Check_Max_Drop", label: "Max Drop", group: "Risk", dir: "down" },
  { key: "Sharpe_Ratio", label: "Sharpe", group: "Returns", dir: "up" },
  { key: "Sortino_Ratio", label: "Sortino", group: "Returns", dir: "up" },
  { key: "Information_Ratio", label: "Info Ratio", group: "Returns", dir: "up" },
  { key: "Annualised_Return", label: "Ann Return", group: "Returns", dir: "up" },
  { key: "Calmar_Ratio", label: "Calmar", group: "Returns", dir: "up" },
  { key: "COVID_Drawdown", label: "COVID DD", group: "Stress", dir: "down" },
  { key: "Ukraine_Drawdown", label: "Ukraine DD", group: "Stress", dir: "down" },
];

const DEFAULT_SELECTED = new Set([
  "AUM (Cr)",
  "Expense Ratio",
  "Tracking Error",
  "Tracking Diff",
  "Avg Volume",
  "VaR95",
  "VaR99",
  "Tail_Risk",
  "Anomaly_Check_Max_Drop",
  "Volatility",
  "Max_Drawdown",
  "Correlation",
  "Tracking_Error_STD",
  "COVID_Drawdown",
  "Ukraine_Drawdown",
]);

const GROUPS = [...new Set(ALL_METRICS.map((m) => m.group))];

const fmtNum = (v, digits = 2) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${(n * 100).toFixed(2)}%`;
};

const toYmd = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const defaultStartFor = (startDate, endDate) => {
  if (!endDate) return "";
  const d = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return startDate || "";
  d.setFullYear(d.getFullYear() - 1);
  const oneYearStart = toYmd(d);
  if (startDate && oneYearStart < startDate) return startDate;
  return oneYearStart;
};

const median = (xs) => {
  const values = xs.filter((v) => v !== null && Number.isFinite(v)).sort((a, b) => a - b);
  return values.length ? values[Math.floor(values.length / 2)] : 0;
};

const stdz = (matrix) => {
  const n = matrix.length;
  const p = matrix[0].length;
  const mu = Array(p).fill(0);
  const sd = Array(p).fill(0);

  for (let j = 0; j < p; j += 1) {
    for (let i = 0; i < n; i += 1) mu[j] += matrix[i][j];
    mu[j] /= n;
    for (let i = 0; i < n; i += 1) sd[j] += (matrix[i][j] - mu[j]) ** 2;
    sd[j] = Math.sqrt(sd[j] / n) || 1;
  }

  return matrix.map((row) => row.map((v, j) => (v - mu[j]) / sd[j]));
};

const cov = (matrix) => {
  const n = matrix.length;
  const p = matrix[0].length;
  const out = Array.from({ length: p }, () => Array(p).fill(0));
  for (let j = 0; j < p; j += 1) {
    for (let k = 0; k < p; k += 1) {
      let total = 0;
      for (let i = 0; i < n; i += 1) total += matrix[i][j] * matrix[i][k];
      out[j][k] = total / Math.max(n - 1, 1);
    }
  }
  return out;
};

const jacobi = (matrix, maxIt = 300) => {
  const p = matrix.length;
  let a = matrix.map((row) => [...row]);
  let v = Array.from({ length: p }, (_, i) => Array.from({ length: p }, (_, j) => (i === j ? 1 : 0)));

  for (let it = 0; it < maxIt; it += 1) {
    let mx = 0;
    let pr = 0;
    let pc = 1;
    for (let i = 0; i < p; i += 1) {
      for (let j = i + 1; j < p; j += 1) {
        if (Math.abs(a[i][j]) > mx) {
          mx = Math.abs(a[i][j]);
          pr = i;
          pc = j;
        }
      }
    }
    if (mx < 1e-10) break;

    const theta = (a[pc][pc] - a[pr][pr]) / (2 * a[pr][pc]);
    const t = Math.sign(theta) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
    const c = 1 / Math.sqrt(t * t + 1);
    const s = t * c;
    const nextA = a.map((row) => [...row]);

    for (let i = 0; i < p; i += 1) {
      nextA[i][pr] = c * a[i][pr] - s * a[i][pc];
      nextA[i][pc] = s * a[i][pr] + c * a[i][pc];
    }
    for (let i = 0; i < p; i += 1) {
      nextA[pr][i] = nextA[i][pr];
      nextA[pc][i] = nextA[i][pc];
    }
    nextA[pr][pr] = c * c * a[pr][pr] - 2 * s * c * a[pr][pc] + s * s * a[pc][pc];
    nextA[pc][pc] = s * s * a[pr][pr] + 2 * s * c * a[pr][pc] + c * c * a[pc][pc];
    nextA[pr][pc] = 0;
    nextA[pc][pr] = 0;
    a = nextA;

    const nextV = v.map((row) => [...row]);
    for (let i = 0; i < p; i += 1) {
      nextV[i][pr] = c * v[i][pr] - s * v[i][pc];
      nextV[i][pc] = s * v[i][pr] + c * v[i][pc];
    }
    v = nextV;
  }

  const eigenvalues = a.map((row, i) => row[i]);
  const order = eigenvalues
    .map((value, index) => [value, index])
    .sort((x, y) => y[0] - x[0])
    .map((x) => x[1]);

  return {
    eigenvalues: order.map((i) => eigenvalues[i]),
    eigenvectors: order.map((i) => v.map((row) => row[i])),
  };
};

const naturalMetricWeights = (eigenvectors, explained, count) => {
  const weights = Array(count).fill(0);
  for (let ki = 0; ki < count; ki += 1) {
    for (let pi = 0; pi < eigenvectors.length; pi += 1) {
      weights[ki] += Math.max(explained[pi] || 0, 0) * eigenvectors[pi][ki] ** 2;
    }
  }
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => w / total);
};

const redistributeWeights = (naturalW, userW, keys) => {
  const pinned = Object.keys(userW);
  const pinnedSum = pinned.reduce((total, key) => total + (userW[key] || 0), 0);
  const remaining = Math.max(0, 1 - pinnedSum);
  const unpinned = keys.filter((key) => !pinned.includes(key));
  const unpinnedNaturalTotal =
    unpinned.reduce((total, key) => total + (naturalW[keys.indexOf(key)] || 0), 0) || 1;

  return keys.map((key, index) => {
    if (pinned.includes(key)) return userW[key] || 0;
    return ((naturalW[index] || 0) / unpinnedNaturalTotal) * remaining;
  });
};

const runPCA = (etfs, selectedKeys, overrides = {}, userWeights = {}) => {
  const data = etfs.map((etf) => {
    const copy = { ...etf };
    const override = overrides[etf.Symbol];
    if (override) {
      if (override.aum !== null && override.aum !== undefined) copy["AUM (Cr)"] = override.aum;
      if (override.te !== null && override.te !== undefined) copy["Tracking Error"] = override.te;
    }
    return copy;
  });

  const keys = selectedKeys.filter((key) => data.some((etf) => etf[key] !== null && etf[key] !== undefined));
  if (keys.length < 2 || data.length < 2) return null;

  const raw = data.map((etf) =>
    keys.map((key) => {
      const v = Number(etf[key]);
      return Number.isFinite(v) ? v : null;
    })
  );
  const medians = keys.map((_, j) => median(raw.map((row) => row[j])));
  const imputed = raw.map((row) => row.map((v, j) => (v === null ? medians[j] : v)));
  const encoded = imputed.map((row) =>
    row.map((v, j) => (INVERT.has(keys[j]) ? 1 / (Math.abs(v) || 1e-9) : v))
  );
  const encodedMedians = keys.map((_, j) => median(encoded.map((row) => row[j])));
  const clean = encoded.map((row) => row.map((v, j) => (Number.isFinite(v) ? v : encodedMedians[j])));
  const standardized = stdz(clean);
  const { eigenvalues, eigenvectors } = jacobi(cov(standardized));
  const total = eigenvalues.reduce((acc, v) => acc + Math.max(v, 0), 0) || 1;
  const explained = eigenvalues.map((v) => Math.max(v, 0) / total);
  const cumulative = explained.map((_, i) => explained.slice(0, i + 1).reduce((a, b) => a + b, 0));
  const nPC = Math.max(1, cumulative.findIndex((v) => v >= 0.85) + 1);
  const loadings = eigenvectors
    .slice(0, Math.min(4, nPC))
    .map((ev) => keys.map((key, j) => ({ key, val: ev[j] })));
  const naturalW = naturalMetricWeights(eigenvectors, explained, keys.length);
  const pinnedMap = Object.fromEntries(
    Object.keys(userWeights)
      .filter((key) => keys.includes(key))
      .map((key) => [key, userWeights[key]])
  );
  const finalW = redistributeWeights(naturalW, pinnedMap, keys);
  const scores = standardized.map((row) => row.reduce((acc, v, j) => acc + v * finalW[j], 0));
  const ranked = scores
    .map((score, i) => ({ ...data[i], _score: score }))
    .sort((a, b) => b._score - a._score)
    .map((etf, i) => ({ ...etf, _rank: i + 1 }));

  return {
    ranked,
    explained,
    cumulative,
    nPC,
    loadings,
    keys,
    naturalW,
    finalW,
    hasUserWeights: Object.keys(pinnedMap).length > 0,
    varExplained: Number(((cumulative[nPC - 1] || 0) * 100).toFixed(1)),
  };
};

function OverrideDialog({ row, existing, onClose, onSave }) {
  const [aum, setAum] = useState(existing?.aum ?? row?.["AUM (Cr)"] ?? "");
  const [te, setTe] = useState(existing?.te ?? row?.["Tracking Error"] ?? "");

  if (!row) return null;

  const save = () => {
    const nextAum = aum === "" ? null : Number(aum);
    const nextTe = te === "" ? null : Number(te);
    onSave(row.Symbol, {
      aum: Number.isFinite(nextAum) ? nextAum : null,
      te: Number.isFinite(nextTe) ? nextTe : null,
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sensitivity Override - {row.Symbol}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="AUM (Cr)"
            type="number"
            value={aum}
            onChange={(e) => setAum(e.target.value)}
            fullWidth
          />
          <TextField
            label="Tracking Error"
            type="number"
            value={te}
            onChange={(e) => setTe(e.target.value)}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            This changes the PCA sensitivity input only. Stored ETF data is not changed.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Rankings() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isAuthenticated, status: authStatus } = useAuth();
  const [benchmarks, setBenchmarks] = useState([]);
  const [selectedBenchmark, setSelectedBenchmark] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loadingBenchmarks, setLoadingBenchmarks] = useState(false);
  const [benchmarkError, setBenchmarkError] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [selected, setSelected] = useState(new Set(DEFAULT_SELECTED));
  const [userWeights, setUserWeights] = useState({});
  const [overrides, setOverrides] = useState({});
  const [editRow, setEditRow] = useState(null);
  const [tab, setTab] = useState("rankings");

  useEffect(() => {
    if (authStatus === "loading") return undefined;

    let active = true;
    const load = async () => {
      setLoadingBenchmarks(true);
      setBenchmarkError("");
      setBenchmarks([]);
      setSelectedBenchmark(null);
      setAnalysis(null);
      setAnalysisError("");
      setOverrides({});
      setUserWeights({});
      setTab("rankings");
      try {
        const data = await apiFetch("/api/v1/auth/etf/analysis/benchmarks");
        if (!active) return;
        const rows = data.rows || [];
        setBenchmarks(rows);
        if (rows.length) {
          const first = rows[0];
          setSelectedBenchmark(first);
          setEndDate(first.tri_end_date || "");
          setStartDate(defaultStartFor(first.tri_start_date, first.tri_end_date));
        } else {
          setStartDate("");
          setEndDate("");
        }
      } catch (err) {
        if (!active) return;
        setBenchmarkError(err.message || "Failed to load benchmarks");
      } finally {
        if (active) setLoadingBenchmarks(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [authStatus, isAuthenticated]);

  const currentEtfs = useMemo(() => analysis?.windows?.[0]?.etfs || [], [analysis]);
  const pca = useMemo(
    () => runPCA(currentEtfs, [...selected], overrides, userWeights),
    [currentEtfs, selected, overrides, userWeights]
  );
  const ranked = pca?.ranked || [];
  const minScore = ranked.length ? Math.min(...ranked.map((row) => row._score)) : 0;
  const maxScore = ranked.length ? Math.max(...ranked.map((row) => row._score)) : 1;
  const scoreRange = maxScore - minScore || 1;

  const screeData = (pca?.explained || []).map((v, i) => ({
    pc: `PC${i + 1}`,
    explained: Number((v * 100).toFixed(2)),
    cumulative: Number(((pca?.cumulative?.[i] || 0) * 100).toFixed(2)),
    selected: i < (pca?.nPC || 0),
  }));

  const calculate = async () => {
    if (!selectedBenchmark || !startDate || !endDate) {
      setAnalysisError("Select benchmark, start date and end date");
      return;
    }

    setLoadingAnalysis(true);
    setAnalysisError("");
    setAnalysis(null);
    setOverrides({});
    setUserWeights({});
    setTab("rankings");

    try {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const data = await apiFetch(
        `/api/v1/auth/etf/analysis/benchmark/${selectedBenchmark.biid}?${qs.toString()}`
      );
      setAnalysis(data);
    } catch (err) {
      setAnalysisError(err.message || "Failed to calculate rankings");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const toggleMetric = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 2) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const saveOverride = (symbol, values) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (values.aum === null && values.te === null) delete next[symbol];
      else next[symbol] = values;
      return next;
    });
    setEditRow(null);
  };

  return (
    <Stack spacing={2.5}>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          background: isDark
            ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(8,47,73,0.86))"
            : "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(224,242,254,0.86))",
        }}
      >
        <Stack spacing={0.8}>
          <Typography variant="h4" sx={{ fontWeight: 950 }}>
            ETF Ranking Dashboard
          </Typography>
          <Typography color="text.secondary">
            Pick a benchmark and custom date range. The app calculates ETF metrics, then ranks funds
            with an interactive PCA model.
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems="center">
          <Autocomplete
            options={benchmarks}
            value={selectedBenchmark}
            loading={loadingBenchmarks}
            onChange={(_, value) => {
              setSelectedBenchmark(value);
              setAnalysis(null);
              if (value) {
                setEndDate(value.tri_end_date || "");
                setStartDate(defaultStartFor(value.tri_start_date, value.tri_end_date));
              }
            }}
            getOptionLabel={(option) => option?.biname || ""}
            sx={{ minWidth: { xs: "100%", lg: 360 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Benchmark"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBenchmarks ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: "100%", lg: 180 } }}
          />
          <TextField
            label="End date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: "100%", lg: 180 } }}
          />
          <Button
            variant="contained"
            onClick={calculate}
            disabled={loadingAnalysis || loadingBenchmarks}
            sx={{ minHeight: 54, px: 3 }}
          >
            {loadingAnalysis ? "Calculating..." : "Calculate"}
          </Button>
        </Stack>
        {benchmarkError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {benchmarkError}
          </Alert>
        ) : null}
        {analysisError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {analysisError}
          </Alert>
        ) : null}
      </Paper>

      {analysis ? (
        <>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <Chip label={analysis.meta?.index_name || "-"} color="primary" sx={{ fontWeight: 900 }} />
              <Chip label={`${analysis.meta?.global_start} to ${analysis.meta?.global_end}`} />
              <Chip label={`${analysis.meta?.n_etfs || 0} ETFs ranked`} />
              <Chip label={`${analysis.meta?.skipped_etfs || 0} skipped`} />
              {pca ? <Chip label={`${pca.varExplained}% variance`} color="success" /> : null}
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="h6" sx={{ fontWeight: 950 }}>
                  Metrics for PCA
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setSelected(new Set(ALL_METRICS.map((m) => m.key)))}>
                    Select all
                  </Button>
                  <Button size="small" onClick={() => setSelected(new Set(DEFAULT_SELECTED))}>
                    Defaults
                  </Button>
                </Stack>
              </Stack>
              {GROUPS.map((group) => (
                <Box key={group}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                    {group}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
                    {ALL_METRICS.filter((metric) => metric.group === group).map((metric) => (
                      <Chip
                        key={metric.key}
                        label={`${metric.label} ${metric.dir === "up" ? "+" : "-"}`}
                        color={selected.has(metric.key) ? "primary" : "default"}
                        variant={selected.has(metric.key) ? "filled" : "outlined"}
                        onClick={() => toggleMetric(metric.key)}
                        sx={{ fontWeight: 800 }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ borderRadius: 4, overflow: "hidden" }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
              <Tab value="rankings" label="Rankings" />
              <Tab value="scree" label="Scree" />
              <Tab value="weights" label="Loadings & Weights" />
            </Tabs>
            <Divider />

            {tab === "rankings" ? (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {!pca ? (
                  <Alert severity="warning">Select at least two usable metrics and calculate again.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {ranked.map((row) => {
                      const pct = ((row._score - minScore) / scoreRange) * 100;
                      return (
                        <Box key={row.Symbol}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography sx={{ width: 42, fontWeight: 950 }}>#{row._rank}</Typography>
                            <Typography sx={{ width: 120, fontWeight: 950 }}>{row.Symbol}</Typography>
                            <Box sx={{ flex: 1, height: 10, borderRadius: 999, bgcolor: "action.hover" }}>
                              <Box
                                sx={{
                                  width: `${Math.max(2, pct)}%`,
                                  height: "100%",
                                  borderRadius: 999,
                                  background: "linear-gradient(90deg, #2563eb, #06b6d4)",
                                }}
                              />
                            </Box>
                            <Typography sx={{ width: 80, textAlign: "right", fontWeight: 900 }}>
                              {row._score >= 0 ? "+" : ""}
                              {row._score.toFixed(3)}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}

                    <Box sx={{ overflowX: "auto" }}>
                      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
                        <Box component="thead">
                          <Box component="tr">
                            {[
                              "Rank",
                              "Symbol",
                              "ETF",
                              "AUM",
                              "ER",
                              "TE",
                              "TD",
                              "Vol",
                              "Sharpe",
                              "Avg Vol",
                              "Actions",
                            ].map((h) => (
                              <Box
                                key={h}
                                component="th"
                                sx={{ textAlign: "left", p: 1, borderBottom: "1px solid", borderColor: "divider" }}
                              >
                                {h}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {ranked.map((row) => (
                            <Box key={row.Symbol} component="tr">
                              <Box component="td" sx={{ p: 1, fontWeight: 900 }}>
                                #{row._rank}
                              </Box>
                              <Box component="td" sx={{ p: 1, fontWeight: 900 }}>
                                {row.Symbol}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {row["ETF Name"] || "-"}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtNum(row["AUM (Cr)"])}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtNum(row["Expense Ratio"], 4)}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtNum(row["Tracking Error"], 4)}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtPct(row["Tracking Diff"])}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtPct(row.Volatility)}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtNum(row.Sharpe_Ratio)}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                {fmtNum(row["Avg Volume"], 0)}
                              </Box>
                              <Box component="td" sx={{ p: 1 }}>
                                <Button size="small" onClick={() => setEditRow(row)}>
                                  Override
                                </Button>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                )}
              </Box>
            ) : null}

            {tab === "scree" ? (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {pca ? (
                  <Stack spacing={2}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {pca.nPC} PCs explain {fmtNum(pca.varExplained, 2)}% of variance. Blue bars are included in the
                      ranking model; grey bars are left out after crossing the 85% cutoff.
                    </Typography>
                    <Box sx={{ height: 360 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={screeData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="pc" />
                          <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                          <Tooltip
                            formatter={(value, name) => [`${fmtNum(value, 2)}%`, name]}
                            labelStyle={{ color: "#0f172a" }}
                          />
                          <Legend />
                          <ReferenceLine
                            y={85}
                            stroke="#22c55e"
                            strokeDasharray="5 4"
                            label={{ value: "85% cutoff", position: "right", fill: "#22c55e", fontSize: 12 }}
                          />
                          <Bar dataKey="explained" name="Explained %">
                            {screeData.map((entry) => (
                              <Cell
                                key={entry.pc}
                                fill={entry.selected ? "#2563eb" : "rgba(148, 163, 184, 0.45)"}
                              />
                            ))}
                          </Bar>
                          <Line
                            dataKey="cumulative"
                            name="Cumulative %"
                            stroke="#06b6d4"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="warning">No PCA output available.</Alert>
                )}
              </Box>
            ) : null}

            {tab === "weights" ? (
              <Box sx={{ p: { xs: 2, md: 3 }, overflowX: "auto" }}>
                {pca ? (
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                    <Box component="thead">
                      <Box component="tr">
                        {["Metric", "Natural W", "Final W", "Override %"].map((h) => (
                          <Box
                            key={h}
                            component="th"
                            sx={{ textAlign: "left", p: 1, borderBottom: "1px solid", borderColor: "divider" }}
                          >
                            {h}
                          </Box>
                        ))}
                        {pca.loadings.map((_, pcIndex) => (
                          <Box
                            key={`pc-${pcIndex}`}
                            component="th"
                            sx={{ textAlign: "left", p: 1, borderBottom: "1px solid", borderColor: "divider" }}
                          >
                            PC{pcIndex + 1} ({((pca.explained[pcIndex] || 0) * 100).toFixed(1)}%)
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {ALL_METRICS.map((metric) => {
                        const idx = pca.keys.indexOf(metric.key);
                        const selectedMetric = idx >= 0;
                        return (
                        <Box key={metric.key} component="tr" sx={{ opacity: selectedMetric ? 1 : 0.45 }}>
                          <Box component="td" sx={{ p: 1, fontWeight: 900 }}>
                            {metric.label}
                          </Box>
                          <Box component="td" sx={{ p: 1 }}>
                            {selectedMetric ? (pca.naturalW[idx] * 100).toFixed(2) : "0.00"}%
                          </Box>
                          <Box component="td" sx={{ p: 1 }}>
                            {selectedMetric ? (pca.finalW[idx] * 100).toFixed(2) : "0.00"}%
                          </Box>
                          <Box component="td" sx={{ p: 1 }}>
                            {selectedMetric ? (
                              <TextField
                                size="small"
                                type="number"
                                value={
                                  userWeights[metric.key] !== undefined
                                    ? Number(userWeights[metric.key] * 100).toFixed(1)
                                    : ""
                                }
                                placeholder={(pca.naturalW[idx] * 100).toFixed(1)}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setUserWeights((prev) => {
                                    const next = { ...prev };
                                    if (raw === "") delete next[metric.key];
                                    else {
                                      const n = Number(raw);
                                      if (Number.isFinite(n)) next[metric.key] = Math.max(0, Math.min(100, n)) / 100;
                                    }
                                    return next;
                                  });
                                }}
                                sx={{ width: 110 }}
                              />
                            ) : (
                              "-"
                            )}
                          </Box>
                          {pca.loadings.map((pc, pcIndex) => (
                            <Box key={pcIndex} component="td" sx={{ p: 1 }}>
                              {selectedMetric && pc[idx]?.val !== undefined ? pc[idx].val.toFixed(3) : "-"}
                            </Box>
                          ))}
                        </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">No PCA output available.</Alert>
                )}
              </Box>
            ) : null}
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
          <Typography color="text.secondary">
            Select a benchmark and date range, then calculate to view rankings.
          </Typography>
        </Paper>
      )}

      {editRow ? (
        <OverrideDialog
          row={editRow}
          existing={overrides[editRow.Symbol]}
          onClose={() => setEditRow(null)}
          onSave={saveOverride}
        />
      ) : null}
    </Stack>
  );
}
